/**
 * @genesis_domain PKCE Authentication Callback Handler (V7.4.16)
 * @genesis_intent Isolated route for handling PKCE code exchange and OAuth callback parameters without router interference. Synchronizes auth state with URL parameters and provides deterministic routing.
 * @genesis_boundaries This component is FORBIDDEN from performing manual auth exchanges, implementing timeouts, or containing business logic. It only observes AuthContext state and routes accordingly.
 * @genesis_ai_trail V9.1: Added forensic logging and session hydration guard. Before navigating to /login on verification failure, performs a direct supabase.auth.getSession() diagnostic call to distinguish between a true auth failure and a TanStack Query cache staleness race condition.
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext.js';
import { supabase } from '@/core/api/supabase.js';

export function AuthCallback() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isInitializing, isAuthenticated, isRecoveryFlow } = useAuth();

    // Synchronous URL error parsing (handles expired magic links, OAuth failures)
    useEffect(() => {
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error || errorDescription) {
            // Immediately navigate to login with error state
            navigate('/login', {
                state: {
                    error: errorDescription || 'Authentication failed. Please try again.',
                },
                replace: true,
            });
        }
    }, [searchParams, navigate]);

    // Synchronization effect: observe auth state changes
    useEffect(() => {
        // If there's a URL error, the synchronous check above handles it
        // Wait for Supabase to finish its background PKCE exchange
        if (!isInitializing) {
            if (isAuthenticated) {
                // Forced password recovery: redirect to dedicated recovery UI
                if (isRecoveryFlow) {
                    navigate('/auth/update-password', { replace: true });
                    return;
                }
                // Successful authentication - navigate to dynamic next path
                const next = searchParams.get('next') || '/';
                navigate(next, { replace: true });
            } else {
                // --- Session Hydration Guard ---
                // Before giving up, perform a direct data-layer lookup to distinguish
                // between a true auth failure and a TanStack Query cache staleness race.
                // This catches scenarios where detectSessionInUrl completed the PKCE
                // exchange but the TanStack Query cache hasn't propagated yet.
                (async () => {
                    const { data: diagnosticData } = await supabase.auth.getSession();

                    if (diagnosticData?.session) {
                        // Session exists in storage but TanStack Query cache was stale.
                        // Rescue the routing flow and proceed to the forward path.
                        const next = searchParams.get('next') || '/';
                        navigate(next, { replace: true });
                        return;
                    }

                    // Forensic logging: capture full diagnostic context before redirecting
                    console.error('[AuthCallback] Verification Lifecycle Crash:', {
                        url: window.location.href,
                        hasCodeParam: window.location.search.includes('code'),
                        sessionSnapshot: diagnosticData,
                        timestamp: new Date().toISOString(),
                    });

                    // Authentication genuinely failed or no session found
                    navigate('/login', {
                        state: {
                            error: 'Verification failed. Please try logging in again.',
                        },
                        replace: true,
                    });
                })();
            }
        }
    }, [isInitializing, isAuthenticated, navigate]);

    // Render full-screen loading spinner while auth state is initializing
    return (
        <div className="h-screen w-screen flex items-center justify-center">
            <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
                <p className="mt-6 text-ink-secondary font-sans text-lg font-medium">
                    Verifying secure login...
                </p>
                <p className="mt-2 text-ink-tertiary font-sans text-sm">
                    Please wait while we complete the authentication process.
                </p>
            </div>
        </div>
    );
}

